import React, { useRef, type RefObject } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import FormikForm from '../../../components/Formik/Form/FormikForm';
import { isValidPassword } from '../../../helpers/validation';
import useApiQuery from '../../../hooks/useApiQuery';

interface PasswordChangeFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormikFormHandle {
  resetForm: () => void;
}

const UserPasswordChange = (): React.JSX.Element => {
  const intl = useIntl();
  const formRef: RefObject<FormikFormHandle> = useRef(null);
  const { makeApiCall: updatePassword } = useApiQuery(
    Auth.updateUserPassword as unknown as (...args: unknown[]) => Promise<unknown>,
    {
      successMessage: 'userPasswordChanged',
    }
  );

  const fields = [
    {
      fieldType: 'password',
      name: 'currentPassword',
      label: 'Current Password',
    },
    {
      fieldType: 'password',
      name: 'newPassword',
      label: 'New Password',
    },
    {
      fieldType: 'password',
      name: 'confirmPassword',
      label: 'Confirm Password',
    },
  ];

  const initialValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Required'),
    newPassword: Yup.string()
      .test('Strong password', intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }), value =>
        value ? isValidPassword(value) : true
      )
      .required('Required'),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref('newPassword'), undefined],
        intl.formatMessage({ id: 'userPasswordMismatch' })
      )
      .required('Required'),
  });

  const handleSubmit = async (formData: PasswordChangeFormData): Promise<void> => {
    const newPasswordData: Record<string, unknown> = {
      password: formData.currentPassword,
      newPassword: formData.newPassword,
    };

    const data: unknown = await updatePassword(newPasswordData);
    if (data !== null) {
      formRef.current?.resetForm();
    }
  };

  return (
    <FormikForm
      formRef={formRef}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit as unknown as (values: Record<string, unknown>) => void}
      submitLabel='Change Password'
    />
  );
};

export default UserPasswordChange;
